import React, { useEffect, useRef, useCallback } from 'react';
import { ChartContentSkeleton } from './DashboardSkeleton';
import { useGoogleCharts } from '../hooks/useGoogleCharts';
import { formatShortDate } from '../utils/formatters';

const LoadingChart = () => <ChartContentSkeleton />;

// --- Accessible Table View ---
const TableView = ({ headers, rows, caption }) => {
    if (rows.length === 0) {
        return <div className="flex items-center justify-center h-full text-slate-500">No data for selected filters.</div>;
    }

    return (
        <div 
            className="h-full overflow-auto text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            tabIndex={0}
            role="region"
            aria-label={`${caption} - scrollable table`}
        >
            <table className="w-full text-left border-collapse">
                <caption className="sr-only">{caption}</caption>
                <thead className="sticky top-0 bg-slate-50 z-10">
                    <tr>
                        {headers.map((header, index) => (
                            <th key={index} scope="col" className="p-2 font-semibold text-slate-600 border-b border-slate-300">
                                {header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, rowIndex) => (
                        <tr key={rowIndex} className="border-b border-slate-200 last:border-b-0 hover:bg-slate-50">
                            {/* Use a row header for the first cell to improve screen reader context */}
                            <th scope="row" className="p-2 text-slate-700 font-medium text-left">
                                {row[0]}
                            </th>
                            {row.slice(1).map((cell, cellIndex) => (
                                <td key={cellIndex} className={`p-2 text-slate-700 ${typeof cell === 'number' ? 'text-right font-mono' : ''}`}>
                                    {typeof cell === 'number' ? cell?.toLocaleString('en-IN') : cell}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// --- Custom Hook for Responsive Charts ---
const useResponsiveChart = (
    drawChart,
    data,
    createChart,
    getChartData,
    getChartOptions,
    view
) => {
    const isLoaded = useGoogleCharts();
    const chartRef = useRef(null);
    const chartInstanceRef = useRef(null);

    const draw = useCallback(() => {
        if (!isLoaded || !chartRef.current || !window.google) return;
        if (chartRef.current.offsetParent === null) return;

        if (!chartInstanceRef.current) {
            chartInstanceRef.current = createChart(chartRef.current);
        }
        
        const chart = chartInstanceRef.current;
        const isDataEmpty = data.length === 0 || (Array.isArray(data) && data.every(d => 'count' in d && d.count === 0));
        
        if (isDataEmpty) {
            chart.clearChart();
            return;
        }

        const chartData = getChartData();
        const dataTable = window.google.visualization.arrayToDataTable(chartData);
        const options = getChartOptions();

        chart.clearChart();
        drawChart(chart, dataTable, options);
    }, [isLoaded, data, createChart, drawChart, getChartData, getChartOptions]);

    useEffect(() => {
        if (chartInstanceRef.current && chartRef.current) {
            chartInstanceRef.current.clearChart();
            chartInstanceRef.current = createChart(chartRef.current);
        }
    }, [createChart]);

    useEffect(() => {
        if (view === 'chart') {
            draw();
        }
        let resizeTimeout;
        const debouncedDraw = () => {
            if (view === 'chart') {
                window.clearTimeout(resizeTimeout);
                resizeTimeout = window.setTimeout(draw, 250);
            }
        };
        window.addEventListener('resize', debouncedDraw);

        return () => {
            window.clearTimeout(resizeTimeout);
            window.removeEventListener('resize', debouncedDraw);
            if (chartInstanceRef.current && window.google) {
                chartInstanceRef.current.clearChart();
            }
        };
    }, [draw, view]);

    const isDataEmpty = data.length === 0 || (Array.isArray(data) && data.every(d => 'count' in d && d.count === 0));
    const showNoDataMessage = isLoaded && isDataEmpty;

    return {
        render: (
            <>
                {!isLoaded && <LoadingChart />}
                {showNoDataMessage && <div className="flex items-center justify-center h-full text-slate-500">No data for selected filters.</div>}
                <div ref={chartRef} className="w-full h-full" style={{ display: isLoaded && !showNoDataMessage ? 'block' : 'none' }} />
            </>
        )
    };
};

// --- Chart Components ---

// Common options for all charts to maintain a consistent style
const commonChartOptions = {
    backgroundColor: 'transparent',
    fontName: 'sans-serif',
    chartArea: {
        left: 60, 
        top: 30,
        width: '85%',
        height: '70%'
    },
    legend: {
        position: 'bottom',
        textStyle: {
            color: '#64748b',
            fontSize: 12
        }
    },
    hAxis: {
        textStyle: {
            color: '#64748b',
            fontSize: 12
        },
        gridlines: {
            color: 'transparent'
        }
    },
    vAxis: {
        textStyle: {
            color: '#64748b',
            fontSize: 12
        },
        gridlines: {
            color: '#e2e8f0'
        },
        minorGridlines: {
            color: 'transparent'
        }
    },
    tooltip: {
        isHtml: true, 
        trigger: 'focus',
        textStyle: {
            color: '#334155'
        },
    },
};

export const LoanDisbursedChart = ({ data, type, view = 'chart' }) => {
    const createChart = useCallback((element) => {
        const google = window.google;
        switch(type) {
            case 'AreaChart': return new google.visualization.AreaChart(element);
            case 'ComboChart':
            default: return new google.visualization.ComboChart(element);
        }
    }, [type]);

    const getChartData = useCallback(() => {
        let processedData = data;
        if (data.length === 1) {
            const singleDate = new Date(`${data[0].date}T00:00:00Z`);
            const dayBefore = new Date(singleDate); dayBefore.setUTCDate(singleDate.getUTCDate() - 1);
            const dayAfter = new Date(singleDate); dayAfter.setUTCDate(singleDate.getUTCDate() + 1);
            processedData = [
                { date: dayBefore.toISOString().split('T')[0], amount: null, count: null },
                data[0],
                { date: dayAfter.toISOString().split('T')[0], amount: null, count: null },
            ];
        }
        return [
            ['Date', 'Loan Amount', 'Loan Count'],
            ...processedData.map(item => {
                const dateParts = item.date.split('-').map(Number);
                const utcDate = new Date(Date.UTC(dateParts[0], dateParts[1] - 1, dateParts[2]));
                return [utcDate, item.amount, item.count];
            }),
        ];
    }, [data]);

    const getChartOptions = useCallback(() => {
        const options = {
            ...commonChartOptions,
            legend: { position: 'top', alignment: 'end' },
            vAxes: {
                0: { title: 'Loan Amount (INR)', format: 'short', minValue: 0, textStyle: commonChartOptions.vAxis.textStyle, gridlines: commonChartOptions.vAxis.gridlines },
                1: { title: 'Loan Count', format: 'decimal', minValue: 0, gridlines: { color: 'transparent' }, textStyle: commonChartOptions.vAxis.textStyle },
            },
            hAxis: { ...commonChartOptions.hAxis, format: 'MMM d', slantedText: true, slantedTextAngle: 45 },
            focusTarget: 'category',
        };
        if (type === 'ComboChart') {
            return {
                ...options,
                seriesType: 'bars',
                series: {
                    0: { targetAxisIndex: 0, type: 'bars', color: '#94a3b8' },
                    1: { targetAxisIndex: 1, type: 'line', color: '#3b82f6' },
                },
            };
        }
        // AreaChart
        return {
            ...options,
            series: {
                0: { targetAxisIndex: 0, color: '#3b82f6' },
                1: { targetAxisIndex: 1, color: '#94a3b8' },
            },
            isStacked: true,
        };
    }, [type]);
    
    const drawChart = useCallback((chart, dataTable, options) => {
        const google = window.google;
        const amountFormatter = new google.visualization.NumberFormat({ prefix: '₹', pattern: '#,##,##0' });
        amountFormatter.format(dataTable, 1);
        const countFormatter = new google.visualization.NumberFormat({ pattern: 'decimal' });
        countFormatter.format(dataTable, 2);
        chart.draw(dataTable, options);
    }, []);

    const { render } = useResponsiveChart(
        drawChart, data, createChart, getChartData, getChartOptions, view
    );

    return (
        <div className="w-full h-full">
            <div style={{ display: view === 'chart' ? 'block' : 'none' }} className="w-full h-full" role="img" aria-label={`Loan Disbursed ${type}`}>
                {render}
            </div>
            <div style={{ display: view === 'table' ? 'block' : 'none' }} className="w-full h-full">
                <TableView
                    caption="Loan Disbursed data table, showing daily disbursed amount and count."
                    headers={['Date', 'Loan Amount (INR)', 'Loan Count']}
                    rows={data.map(item => [formatShortDate(item.date), item.amount, item.count])}
                />
            </div>
        </div>
    );
};

const loanStatusColors = [
    '#3b82f6', '#a855f7', '#8b5cf6', '#ef4444', '#f97316', '#78716c', 
    '#eab308', '#22c55e', '#14b8a6', '#06b6d4', '#10b981'
];

export const LoanByStatusChart = ({ data, type, view = 'chart' }) => {
    const createChart = useCallback((element) => {
        const google = window.google;
        switch (type) {
            case 'BarChart': return new google.visualization.BarChart(element);
            case 'PieChart': default: return new google.visualization.PieChart(element);
        }
    }, [type]);

    const getChartData = useCallback(() => {
        const total = data.reduce((sum, item) => sum + item.count, 0);
        const headers = [
            'Status', 'Count', { role: 'tooltip', type: 'string', p: { html: true } },
        ];
        if (type === 'BarChart') {
            headers.push({ role: 'style', type: 'string' });
        }
        return [
            headers,
            ...data.map((item, index) => {
                const percentage = total > 0 ? ((item.count / total) * 100).toFixed(1) : '0.0';
                const tooltipHtml = `<div style="padding:10px; font-family: sans-serif; font-size: 12px;"><strong>${item.status}</strong><br/>Count: <strong>${item?.count?.toLocaleString('en-IN')}</strong> (${percentage}%)</div>`;
                const row = [item.status, item.count, tooltipHtml];
                if (type === 'BarChart') {
                    row.push(loanStatusColors[index % loanStatusColors.length]);
                }
                return row;
            }),
        ];
    }, [data, type]);
    
    const getChartOptions = useCallback(() => {
        const options = {
            ...commonChartOptions,
            colors: loanStatusColors,
            chartArea: { left: 10, top: 20, width: '95%', height: '75%' },
        };
        if (type === 'PieChart') {
            return { ...options, pieHole: 0.4 };
        }
        return { ...options, chartArea: commonChartOptions.chartArea, legend: { position: 'none' } };
    }, [type]);

    const drawChart = useCallback((chart, dataTable, options) => {
        chart.draw(dataTable, options);
    }, []);

    const { render } = useResponsiveChart(
        drawChart, data, createChart, getChartData, getChartOptions, view
    );
    
    const total = data.reduce((sum, item) => sum + item.count, 0);

    return (
        <div className="w-full h-full">
            <div style={{ display: view === 'chart' ? 'block' : 'none' }} className="w-full h-full" role="img" aria-label={`Loan by Status ${type}`}>
                {render}
            </div>
            <div style={{ display: view === 'table' ? 'block' : 'none' }} className="w-full h-full">
                <TableView
                    caption="Loan by Status data table, showing counts and percentages for each loan status."
                    headers={['Status', 'Count', 'Percentage']}
                    rows={data.map(item => [item.status, item.count, total > 0 ? `${((item.count / total) * 100).toFixed(1)}%` : '0.0%'])}
                />
            </div>
        </div>
    );
};

export const LeadTrendChart = ({ data, type, view = 'chart' }) => {
    const createChart = useCallback((element) => {
        const google = window.google;
        switch (type) {
            case 'AreaChart': return new google.visualization.AreaChart(element);
            case 'LineChart': default: return new google.visualization.LineChart(element);
        }
    }, [type]);
    
    const getChartData = useCallback(() => {
        let processedData = data;
        if (data.length === 1) {
            const singleDate = new Date(`${data[0].date}T00:00:00Z`);
            const dayBefore = new Date(singleDate); dayBefore.setUTCDate(singleDate.getUTCDate() - 1);
            const dayAfter = new Date(singleDate); dayAfter.setUTCDate(singleDate.getUTCDate() + 1);
            processedData = [
                { date: dayBefore.toISOString().split('T')[0], totalLeads: null, convertedBorrowers: null, rejected: null, pending: null },
                data[0],
                { date: dayAfter.toISOString().split('T')[0], totalLeads: null, convertedBorrowers: null, rejected: null, pending: null },
            ];
        }
        return [
            ['Date', 'Total Leads', 'Converted', 'Pending'],
            ...processedData.map(item => {
                const dateParts = item.date.split('-').map(Number);
                const utcDate = new Date(Date.UTC(dateParts[0], dateParts[1] - 1, dateParts[2]));
                return [utcDate, item.totalLeads, item.convertedBorrowers, item.pending];
            }),
        ];
    }, [data]);

    const getChartOptions = useCallback(() => ({
        ...commonChartOptions,
        colors: ['#3b82f6', '#10b981', '#f59e0b'],
        legend: { position: 'top', alignment: 'end' },
        vAxis: { ...commonChartOptions.vAxis, title: 'Count', format: 'decimal', minValue: 0 },
        hAxis: { ...commonChartOptions.hAxis, format: 'MMM d', slantedText: true, slantedTextAngle: 45 },
        focusTarget: 'category',
        isStacked: type === 'AreaChart',
    }), [type]);

    const drawChart = useCallback((chart, dataTable, options) => {
        chart.draw(dataTable, options);
    }, []);

    const { render } = useResponsiveChart(
        drawChart, data, createChart, getChartData, getChartOptions, view
    );
    
    return (
        <div className="w-full h-full">
            <div style={{ display: view === 'chart' ? 'block' : 'none' }} className="w-full h-full" role="img" aria-label={`Lead Trends ${type}`}>
                {render}
            </div>
            <div style={{ display: view === 'table' ? 'block' : 'none' }} className="w-full h-full">
                <TableView
                    caption="Lead Trends data table, showing daily counts for total leads, converted borrowers, and pending applications."
                    headers={['Date', 'Total Leads', 'Converted', 'Pending']}
                    rows={data.map(item => [formatShortDate(item.date), item.totalLeads, item.convertedBorrowers, item.pending])}
                />
            </div>
        </div>
    );
};

export const BorrowerCompletionChart = ({ data, type, view = 'chart' }) => {
    const createChart = useCallback((element) => {
        const google = window.google;
        switch (type) {
            case 'BarChart': return new google.visualization.BarChart(element);
            case 'PieChart': default: return new google.visualization.PieChart(element);
        }
    }, [type]);
    
    const getChartData = useCallback(() => {
        const total = data.reduce((sum, item) => sum + item.count, 0);
        return [
            ['Status', 'Count', { role: 'tooltip', type: 'string', p: { html: true } }],
            ...data.map(item => {
                const percentage = total > 0 ? ((item.count / total) * 100).toFixed(1) : '0.0';
                const tooltipHtml = `<div style="padding:10px; font-family: sans-serif; font-size: 12px;"><strong>${item.status}</strong><br/>Count: <strong>${item?.count?.toLocaleString('en-IN')}</strong> (${percentage}%)</div>`;
                return [item.status, item.count, tooltipHtml];
            }),
        ];
    }, [data]);

    const getChartOptions = useCallback(() => {
        const options = {
            ...commonChartOptions,
            colors: ['#3b82f6', '#94a3b8'],
            chartArea: { left: 10, top: 20, width: '95%', height: '75%' },
        };
        if (type === 'PieChart') {
            return { ...options, pieHole: 0.4 };
        }
        return { ...options, chartArea: commonChartOptions.chartArea, legend: { position: 'none' } };
    }, [type]);

    const drawChart = useCallback((chart, dataTable, options) => {
        chart.draw(dataTable, options);
    }, []);
    
    const { render } = useResponsiveChart(
        drawChart, data, createChart, getChartData, getChartOptions, view
    );
    
    const total = data.reduce((sum, item) => sum + item.count, 0);

    return (
        <div className="w-full h-full">
            <div style={{ display: view === 'chart' ? 'block' : 'none' }} className="w-full h-full" role="img" aria-label={`Borrower Completion Status ${type}`}>
                {render}
            </div>
            <div style={{ display: view === 'table' ? 'block' : 'none' }} className="w-full h-full">
                <TableView
                    caption="Borrower Completion Status data table, showing counts and percentages for complete and incomplete applications."
                    headers={['Status', 'Count', 'Percentage']}
                    rows={data.map(item => [item.status, item.count, total > 0 ? `${((item.count / total) * 100).toFixed(1)}%` : '0.0%'])}
                />
            </div>
        </div>
    );
};